// CategoryList.jsx - Enhanced with theme and animations
import React, { useEffect, useState } from 'react'
import { summaryApi } from '../../common'
import { Link } from 'react-router-dom'
import { useTheme } from '../../components/Theme/ThemeProvider'
import { ChevronLeft, ChevronRight } from 'lucide-react'

function CategoryList() {
    const [categoryProduct, SetCategoryProduct] = useState([])
    const [loading, setLoading] = useState(false)
    const { colors } = useTheme()
    const scrollRef = React.useRef(null)

    const categoryLoading = new Array(13).fill(null)

    const fetchCategoryProduct = async () => {
        setLoading(true)
        try {
            const response = await fetch(summaryApi.categoryProduct.url, {
                method: summaryApi.categoryProduct.method,
                credentials: "include"
            })
            const responseData = await response.json()
            SetCategoryProduct(responseData?.data || [])
        } catch (error) {
            console.error('Error fetching categories:', error)
        }
        setLoading(false)
    }

    useEffect(() => {
        fetchCategoryProduct()
    }, [])

    const scrollLeft = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' })
        }
    }

    const scrollRight = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' })
        }
    }

    return (
        <div className='relative group'>
            {/* Scroll Buttons */}
            <button
                onClick={scrollLeft}
                className='absolute -left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110'
                style={{
                    backgroundColor: colors.primary,
                    color: 'white',
                    boxShadow: `0 4px 12px ${colors.shadow}`
                }}
            >
                <ChevronLeft size={20} />
            </button>

            <button
                onClick={scrollRight}
                className='absolute -right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110'
                style={{
                    backgroundColor: colors.primary,
                    color: 'white',
                    boxShadow: `0 4px 12px ${colors.shadow}`
                }}
            >
                <ChevronRight size={20} />
            </button>

            {/* Categories Container */}
            <div 
                ref={scrollRef}
                className='flex items-center gap-6 overflow-x-auto scrollbar-none px-2'
            >
                {loading ? (
                    categoryLoading.map((item, index) => (
                        <div
                            key={"categoryLoading" + index}
                            className="flex-shrink-0 flex flex-col items-center gap-3 animate-pulse"
                        >
                            <div
                                className="w-20 h-20 md:w-24 md:h-24 rounded-full shadow-lg flex items-center justify-center"
                                style={{
                                    backgroundColor: colors.bgSecondary,
                                    border: `2px solid ${colors.border}`
                                }}
                            />
                            <div className='h-4 w-16 rounded' style={{ backgroundColor: colors.bgTertiary }} />
                        </div>
                    ))
                ) : (
                    categoryProduct.map((product, index) => (
                        <Link
                            to={"/product-category?category=" + product?.productsCategory}
                            key={index}
                            className='flex-shrink-0 group/item'
                        >
                            <div className='flex flex-col items-center gap-3'>
                                {/* Category Circle */}
                                <div
                                    className='relative w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-2xl cursor-pointer overflow-hidden'
                                    style={{
                                        background: `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.accent}20 100%)`,
                                        border: `3px solid ${colors.primary}`,
                                        backdropFilter: 'blur(10px)'
                                    }}
                                >
                                    {/* Animated background glow */}
                                    <div
                                        className='absolute inset-0 rounded-full opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 animate-pulse'
                                        style={{
                                            background: `radial-gradient(circle, ${colors.primary}40 0%, transparent 70%)`
                                        }}
                                    />
                                    
                                    {/* Product Image Container */}
                                    <div className='relative w-14 h-14 md:w-16 md:h-16 flex items-center justify-center overflow-hidden rounded-full'>
                                        <img
                                            src={product?.productImage[0]}
                                            alt={product.productsCategory}
                                            className='w-full h-full object-contain p-1 group-hover/item:scale-110 transition-transform duration-300 relative z-10'
                                            style={{
                                                filter: 'brightness(1.1) contrast(1.1)',
                                                mixBlendMode: 'multiply'
                                            }}
                                            onError={(e) => {
                                                // Fallback if image fails to load
                                                e.target.style.display = 'none'
                                                e.target.nextSibling.style.display = 'flex'
                                            }}
                                        />
                                        {/* Fallback for missing/broken images */}
                                        <div 
                                            className='hidden w-full h-full items-center justify-center text-xs font-bold capitalize text-center'
                                            style={{ 
                                                color: colors.primary,
                                                backgroundColor: `${colors.primary}15`
                                            }}
                                        >
                                            {product?.productsCategory?.split(' ')[0]}
                                        </div>
                                    </div>
                                </div>

                                {/* Category Name */}
                                <div className='text-center'>
                                    <h2 
                                        className='text-xs md:text-sm font-bold capitalize transition-all duration-300 group-hover/item:scale-105'
                                        style={{ color: colors.text }}
                                    >
                                        {product?.productsCategory}
                                    </h2>
                                </div>
                            </div>
                        </Link>
                    ))
                )}
            </div>
        </div>
    )
}

export default CategoryList